import * as React from 'react';
import { MapPinned } from 'lucide-react';
import { Button, Input, Label, Spinner } from '@aimmo/ui';
import { addCriteria } from '../lib/criteria.js';
import { CriteriaPicker } from './criteria-picker.js';
import { nearbyComuni, resolveComune, searchComuni } from './geo-lookups.js';

export interface DemandComuniFieldProps {
  value: unknown;
  onChange: (value: string[]) => void;
  disabled: boolean;
  /** The id the field's label points at, handed to the comune search. */
  id?: string;
}

/**
 * A demand's target comuni, and their neighbours on request.
 *
 * "A Monza o lì intorno" is how a buyer says it, and before this the agent
 * typed the surrounding comuni one at a time. The ring is drawn around every
 * comune already chosen, from the ISTAT centroids (`geo.comuniNearby`), and
 * lands as ordinary chips: each one can be taken out again, and matching
 * reads the result as the plain list it always did.
 */
export function DemandComuniField({ value, onChange, disabled, id }: DemandComuniFieldProps) {
  const selected = React.useMemo(() => (Array.isArray(value) ? (value as string[]) : []), [value]);
  const [radiusKm, setRadiusKm] = React.useState(10);
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const radiusId = React.useId();

  const addNearby = async () => {
    setPending(true);
    setError(null);
    try {
      const ring = await nearbyComuni(selected, radiusKm);
      onChange(
        addCriteria(
          selected,
          ring.map((comune) => comune.id),
        ),
      );
    } catch {
      setError('Non è stato possibile trovare i comuni vicini.');
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="grid gap-2">
      <CriteriaPicker
        id={id}
        value={selected}
        onChange={onChange}
        disabled={disabled}
        search={searchComuni}
        resolve={resolveComune}
        placeholder="Cerca comune…"
      />

      {selected.length > 0 && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="grid gap-1">
            <Label htmlFor={radiusId} className="text-secondary text-muted">
              Raggio dei comuni vicini (km)
            </Label>
            <Input
              id={radiusId}
              type="number"
              min={1}
              max={50}
              step={1}
              className="w-24"
              value={radiusKm}
              disabled={disabled || pending}
              onChange={(event) => setRadiusKm(Number(event.target.value) || 1)}
            />
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={disabled || pending}
            onClick={() => void addNearby()}
          >
            {pending ? <Spinner className="text-current" /> : <MapPinned />}
            Aggiungi i comuni vicini
          </Button>
        </div>
      )}

      {error && <p className="text-secondary text-danger">{error}</p>}
    </div>
  );
}
